/**
 * acadadminBuilder — sub-container do slice acadadmin (admin acadêmico).
 *
 * Iniciado em G.5.1 com DocumentoTipo. Cresceu pra Documento, Modelos, Emissão,
 * Protocolo (cadastros+fila), Histórico, Certificado, Recurso e Calendário.
 *
 * @since G.5.1
 */
import type { $Fetch } from 'ofetch';

import { HttpDocumentoTipoGateway } from '@/infrastructure/http/HttpDocumentoTipoGateway';
import { HttpDocumentoAprovacaoGateway } from '@/infrastructure/http/HttpDocumentoAprovacaoGateway';
import { HttpDocumentoModeloGateway } from '@/infrastructure/http/HttpDocumentoModeloGateway';
import { HttpDocumentoEmissaoGateway } from '@/infrastructure/http/HttpDocumentoEmissaoGateway';
import {
  HttpProtocoloSetorGateway, HttpProtocoloAssuntoGateway,
} from '@/infrastructure/http/HttpProtocoloCadastrosGateway';
import { HttpProtocoloAdminGateway } from '@/infrastructure/http/HttpProtocoloAdminGateway';
import { HttpHistoricoEscolarGateway } from '@/infrastructure/http/HttpHistoricoEscolarGateway';
import { HttpCertificadoGateway } from '@/infrastructure/http/HttpCertificadoGateway';
import { HttpAgendamentoRecursoGateway } from '@/infrastructure/http/HttpAgendamentoRecursoGateway';
import { HttpAgendamentoGateway } from '@/infrastructure/http/HttpAgendamentoGateway';
import { HttpSolicitacaoConclusaoGateway } from '@/infrastructure/http/HttpSolicitacaoConclusaoGateway';
import { HttpLgpdAdminGateway } from '@/infrastructure/http/HttpLgpdAdminGateway';
import { HttpAcademicoDashboardGateway } from '@/infrastructure/http/HttpAcademicoDashboardGateway';
import { GetAcademicoDashboard } from '@/application/usecase/GetAcademicoDashboard';

import {
  ListDocumentoTiposAdmin, GetDocumentoTipo, CreateDocumentoTipo,
  UpdateDocumentoTipo, DeactivateDocumentoTipo,
} from '@/application/usecase/DocumentoTipoUseCaseImpl';
import {
  ListFilaValidacao, AprovarDocumento, NegarDocumento, PedirComplementacaoDocumento,
} from '@/application/usecase/DocumentoAprovacaoUseCaseImpl';
import {
  ListDocumentoModelos, GetDocumentoModelo, CreateDocumentoModelo,
  UpdateDocumentoModelo, DeactivateDocumentoModelo,
} from '@/application/usecase/DocumentoModeloUseCaseImpl';
import {
  ListDocumentosEmitidosByAluno, GetDocumentoEmitido, EmitirDocumento,
} from '@/application/usecase/DocumentoEmissaoUseCaseImpl';
import {
  ListProtocoloSetores, GetProtocoloSetor, CreateProtocoloSetor,
  UpdateProtocoloSetor, DeactivateProtocoloSetor,
  ListProtocoloAssuntos, GetProtocoloAssunto, CreateProtocoloAssunto,
  UpdateProtocoloAssunto, DeactivateProtocoloAssunto,
} from '@/application/usecase/ProtocoloCadastrosUseCaseImpl';
import {
  ListFilaPorSetor, ClaimProtocolo,
} from '@/application/usecase/ProtocoloAdminUseCaseImpl';
import { GetHistoricoEscolar } from '@/application/usecase/HistoricoEscolarUseCaseImpl';
import {
  ListCertificados, ListCertificadosByAluno, GetCertificado,
  MarcarCertificadoBaixado, RevogarCertificado,
} from '@/application/usecase/CertificadoUseCaseImpl';
import {
  ListAgendamentoRecursos, GetAgendamentoRecurso, CreateAgendamentoRecurso,
  UpdateAgendamentoRecurso, DeactivateAgendamentoRecurso,
} from '@/application/usecase/AgendamentoRecursoUseCaseImpl';
import {
  ListCalendario, GetAgendamento, CriarAgendamento, ConfirmarAgendamento,
  MarcarAgendamentoRealizado, MarcarAgendamentoNoShow, CancelarAgendamento,
} from '@/application/usecase/AgendamentoUseCaseImpl';
import {
  ListSolicitacoesConclusao, GetSolicitacaoConclusao,
  ListEtapasSolicitacaoConclusao, ListDocumentosSolicitacaoConclusao,
  ListHistoricoSolicitacaoConclusao,
  FilaSolicitacaoConclusao, ClaimEtapaSolicitacaoConclusao,
  DecidirEtapaSolicitacaoConclusao, PedirComplementacaoSolicitacaoConclusao,
  RegistrarAcademicoSolicitacaoConclusao, ReenviarSolicitacaoConclusao,
  ReverterSolicitacaoConclusao,
} from '@/application/usecase/SolicitacaoConclusaoUseCaseImpl';
import {
  ListLgpdSolicitacoesAdmin, ProcessarLgpdSolicitacao,
} from '@/application/usecase/LgpdAdminUseCaseImpl';
import {
  HttpHorasConfigGateway, HttpHorasLancamentoGateway,
} from '@/infrastructure/http/HttpHorasComplementaresGateways';
import {
  ListHorasConfigs, CreateHorasConfig, UpdateHorasConfig, DeactivateHorasConfig,
  ListHorasCategorias, CreateHorasCategoria, UpdateHorasCategoria, DeactivateHorasCategoria,
  ListHorasFila, GetHorasLancamento, AprovarHorasLancamento, RejeitarHorasLancamento,
  DownloadHorasComprovante,
} from '@/application/usecase/HorasComplementaresUseCaseImpl';

export function buildAcadadminSlice(httpClient: $Fetch) {
  const documentoTipoGateway = new HttpDocumentoTipoGateway(httpClient);
  const horasConfigGateway = new HttpHorasConfigGateway(httpClient);
  const horasLancamentoGateway = new HttpHorasLancamentoGateway(httpClient);
  const documentoAprovacaoGateway = new HttpDocumentoAprovacaoGateway(httpClient);
  const documentoModeloGateway = new HttpDocumentoModeloGateway(httpClient);
  const documentoEmissaoGateway = new HttpDocumentoEmissaoGateway(httpClient);
  const protocoloSetorGateway = new HttpProtocoloSetorGateway(httpClient);
  const protocoloAssuntoGateway = new HttpProtocoloAssuntoGateway(httpClient);
  const protocoloAdminGateway = new HttpProtocoloAdminGateway(httpClient);
  const historicoEscolarGateway = new HttpHistoricoEscolarGateway(httpClient);
  const certificadoGateway = new HttpCertificadoGateway(httpClient);
  const agendamentoRecursoGateway = new HttpAgendamentoRecursoGateway(httpClient);
  const agendamentoGateway = new HttpAgendamentoGateway(httpClient);
  const solicitacaoConclusaoGateway = new HttpSolicitacaoConclusaoGateway(httpClient);
  const lgpdAdminGateway = new HttpLgpdAdminGateway(httpClient);
  const academicoDashboardGateway = new HttpAcademicoDashboardGateway(httpClient);

  const useCases = {
    listHorasConfigs: new ListHorasConfigs(horasConfigGateway),
    createHorasConfig: new CreateHorasConfig(horasConfigGateway),
    updateHorasConfig: new UpdateHorasConfig(horasConfigGateway),
    deactivateHorasConfig: new DeactivateHorasConfig(horasConfigGateway),
    listHorasCategorias: new ListHorasCategorias(horasConfigGateway),
    createHorasCategoria: new CreateHorasCategoria(horasConfigGateway),
    updateHorasCategoria: new UpdateHorasCategoria(horasConfigGateway),
    deactivateHorasCategoria: new DeactivateHorasCategoria(horasConfigGateway),
    listHorasFila: new ListHorasFila(horasLancamentoGateway),
    getHorasLancamento: new GetHorasLancamento(horasLancamentoGateway),
    aprovarHorasLancamento: new AprovarHorasLancamento(horasLancamentoGateway),
    rejeitarHorasLancamento: new RejeitarHorasLancamento(horasLancamentoGateway),
    downloadHorasComprovante: new DownloadHorasComprovante(horasLancamentoGateway),
    listDocumentoTiposAdmin: new ListDocumentoTiposAdmin(documentoTipoGateway),
    getDocumentoTipo: new GetDocumentoTipo(documentoTipoGateway),
    createDocumentoTipo: new CreateDocumentoTipo(documentoTipoGateway),
    updateDocumentoTipo: new UpdateDocumentoTipo(documentoTipoGateway),
    deactivateDocumentoTipo: new DeactivateDocumentoTipo(documentoTipoGateway),
    listFilaValidacao: new ListFilaValidacao(documentoAprovacaoGateway),
    aprovarDocumento: new AprovarDocumento(documentoAprovacaoGateway),
    negarDocumento: new NegarDocumento(documentoAprovacaoGateway),
    pedirComplementacaoDocumento: new PedirComplementacaoDocumento(documentoAprovacaoGateway),
    listDocumentoModelos: new ListDocumentoModelos(documentoModeloGateway),
    getDocumentoModelo: new GetDocumentoModelo(documentoModeloGateway),
    createDocumentoModelo: new CreateDocumentoModelo(documentoModeloGateway),
    updateDocumentoModelo: new UpdateDocumentoModelo(documentoModeloGateway),
    deactivateDocumentoModelo: new DeactivateDocumentoModelo(documentoModeloGateway),
    listDocumentosEmitidosByAluno: new ListDocumentosEmitidosByAluno(documentoEmissaoGateway),
    getDocumentoEmitido: new GetDocumentoEmitido(documentoEmissaoGateway),
    emitirDocumento: new EmitirDocumento(documentoEmissaoGateway),
    listProtocoloSetores: new ListProtocoloSetores(protocoloSetorGateway),
    getProtocoloSetor: new GetProtocoloSetor(protocoloSetorGateway),
    createProtocoloSetor: new CreateProtocoloSetor(protocoloSetorGateway),
    updateProtocoloSetor: new UpdateProtocoloSetor(protocoloSetorGateway),
    deactivateProtocoloSetor: new DeactivateProtocoloSetor(protocoloSetorGateway),
    listProtocoloAssuntos: new ListProtocoloAssuntos(protocoloAssuntoGateway),
    getProtocoloAssunto: new GetProtocoloAssunto(protocoloAssuntoGateway),
    createProtocoloAssunto: new CreateProtocoloAssunto(protocoloAssuntoGateway),
    updateProtocoloAssunto: new UpdateProtocoloAssunto(protocoloAssuntoGateway),
    deactivateProtocoloAssunto: new DeactivateProtocoloAssunto(protocoloAssuntoGateway),
    listFilaPorSetor: new ListFilaPorSetor(protocoloAdminGateway),
    claimProtocolo: new ClaimProtocolo(protocoloAdminGateway),
    getHistoricoEscolar: new GetHistoricoEscolar(historicoEscolarGateway),
    listCertificados: new ListCertificados(certificadoGateway),
    listCertificadosByAluno: new ListCertificadosByAluno(certificadoGateway),
    getCertificado: new GetCertificado(certificadoGateway),
    marcarCertificadoBaixado: new MarcarCertificadoBaixado(certificadoGateway),
    revogarCertificado: new RevogarCertificado(certificadoGateway),
    listAgendamentoRecursos: new ListAgendamentoRecursos(agendamentoRecursoGateway),
    getAgendamentoRecurso: new GetAgendamentoRecurso(agendamentoRecursoGateway),
    createAgendamentoRecurso: new CreateAgendamentoRecurso(agendamentoRecursoGateway),
    updateAgendamentoRecurso: new UpdateAgendamentoRecurso(agendamentoRecursoGateway),
    deactivateAgendamentoRecurso: new DeactivateAgendamentoRecurso(agendamentoRecursoGateway),
    listCalendario: new ListCalendario(agendamentoGateway),
    getAgendamento: new GetAgendamento(agendamentoGateway),
    criarAgendamento: new CriarAgendamento(agendamentoGateway),
    confirmarAgendamento: new ConfirmarAgendamento(agendamentoGateway),
    marcarAgendamentoRealizado: new MarcarAgendamentoRealizado(agendamentoGateway),
    marcarAgendamentoNoShow: new MarcarAgendamentoNoShow(agendamentoGateway),
    cancelarAgendamento: new CancelarAgendamento(agendamentoGateway),
    listSolicitacoesConclusao: new ListSolicitacoesConclusao(solicitacaoConclusaoGateway),
    getSolicitacaoConclusao: new GetSolicitacaoConclusao(solicitacaoConclusaoGateway),
    listEtapasSolicitacaoConclusao: new ListEtapasSolicitacaoConclusao(solicitacaoConclusaoGateway),
    listDocumentosSolicitacaoConclusao: new ListDocumentosSolicitacaoConclusao(solicitacaoConclusaoGateway),
    listHistoricoSolicitacaoConclusao: new ListHistoricoSolicitacaoConclusao(solicitacaoConclusaoGateway),
    filaSolicitacaoConclusao: new FilaSolicitacaoConclusao(solicitacaoConclusaoGateway),
    claimEtapaSolicitacaoConclusao: new ClaimEtapaSolicitacaoConclusao(solicitacaoConclusaoGateway),
    decidirEtapaSolicitacaoConclusao: new DecidirEtapaSolicitacaoConclusao(solicitacaoConclusaoGateway),
    pedirComplementacaoSolicitacaoConclusao: new PedirComplementacaoSolicitacaoConclusao(solicitacaoConclusaoGateway),
    registrarAcademicoSolicitacaoConclusao: new RegistrarAcademicoSolicitacaoConclusao(solicitacaoConclusaoGateway),
    reenviarSolicitacaoConclusao: new ReenviarSolicitacaoConclusao(solicitacaoConclusaoGateway),
    reverterSolicitacaoConclusao: new ReverterSolicitacaoConclusao(solicitacaoConclusaoGateway),
    listLgpdSolicitacoesAdmin: new ListLgpdSolicitacoesAdmin(lgpdAdminGateway),
    processarLgpdSolicitacao: new ProcessarLgpdSolicitacao(lgpdAdminGateway),
    getAcademicoDashboard: new GetAcademicoDashboard(academicoDashboardGateway),
  };

  return { useCases };
}
